import Paragraph from "immigram-ui-cmon/src/components/Paragraph/Paragraph";
import {
  IMarker,
  ISelection,
} from "immigram-ui-cmon/src/components/RichEditor/RichEditor";
import TextArea, {
  TextAreaMinRows,
} from "immigram-ui-cmon/src/components/TextArea/TextArea";
import { useTranslation } from "next-i18next";
import React, { ReactNode, useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { ARRAY_FIELD_NAME } from "@/lib/hookForm";
import { parseProseMirrorSchema } from "@/lib/proseMirror";
import { StopWord } from "@/lib/stopWords";

import { VoiceRecorderButton } from "./VoiceRecorderButton";

export interface ControlledTextAreaProps<T extends FieldValues> {
  control: Control<T>;
  index?: number;
  fieldName: string;
  label?: React.ReactNode;
  rules?: Record<string, any>;
  minRows?: number;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  freeze?: boolean;
  onHelpClick?: () => void;
  onChange?: (
    fieldName: string,
    value: string,
    arrayFieldIndex?: number
  ) => void;
  widthFull?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  markers?: IMarker[];
  stopWordsList?: StopWord[];
  onTextSelectionUpdate?: (selection: ISelection) => void;
  onMarkerSelected?: (markerId: string) => void;
  onDisabledToggle?: (value: boolean) => void;
  after?: (focused: boolean) => ReactNode;
  example?: ReactNode | string;
  fullExample?: boolean;
  onlyDigits?: boolean;
  enableVoiceRecorder?: boolean;
}

const ControlledTextAreaWithRaisingLabel = <
  T extends Record<string, any> = Record<string, any>
>(
  props: ControlledTextAreaProps<T>
) => {
  const { t } = useTranslation();

  // Используем useState на уровне компонента для хранения состояния транскрипции по ключам полей
  const fieldKey =
    props.index === undefined
      ? props.fieldName
      : `${ARRAY_FIELD_NAME}.${props.index}.${props.fieldName}`;
  const [isTranscribingMap, setIsTranscribingMap] = useState<
    Record<string, boolean>
  >({});

  const isTranscribing = isTranscribingMap[fieldKey] || false;
  const setIsTranscribing = (value: boolean) => {
    setIsTranscribingMap((prev) => ({ ...prev, [fieldKey]: value }));
  };

  return (
    <Controller
      control={props.control}
      rules={props.rules}
      name={
        (props.index === undefined
          ? props.fieldName
          : `${ARRAY_FIELD_NAME}.${props.index}.${props.fieldName}`) as Path<T> // TODO: Fix generics for fieldName
      }
      render={({ field: { onChange, value, ref } }) => {
        // По умолчанию голосовой ввод включен, если явно не передан enableVoiceRecorder=false.
        // Для числовых полей (onlyDigits=true) голосовой ввод отключаем.
        const enableVoiceRecorder =
          props.enableVoiceRecorder !== false &&
          !props.disabled &&
          !props.freeze &&
          !props.onlyDigits;

        // Создаем компонент кнопки заранее, чтобы он всегда был доступен
        const voiceRecorderButton = enableVoiceRecorder ? (
          <VoiceRecorderButton
            onTranscriptionComplete={(text) => {
              onChange(text);
              if (props.onChange && typeof text === "string") {
                props.onChange(props.fieldName, text, props.index);
              }
            }}
            disabled={props.disabled || props.freeze}
            currentValue={value || ""}
            onTranscribingChange={setIsTranscribing}
            fieldName={props.fieldName}
          />
        ) : undefined;

        return (
          <>
            <div className="relative">
              <TextArea
                onMarkerSelected={props.onMarkerSelected}
                onTextSelectionUpdate={props.onTextSelectionUpdate}
                markers={props.markers}
                name={props.fieldName}
                label={props.label || t("application:common.description")}
                placeholder={props.placeholder}
                ref={ref}
                value={value ? value : ""}
                error={props.error}
                freeze={props.freeze}
                disabled={props.disabled}
                onDisabledToggle={props.onDisabledToggle}
                after={props.after}
                minRows={props.minRows ?? TextAreaMinRows.OneLine}
                stopWordsList={props.stopWordsList}
                enableVoiceRecorder={enableVoiceRecorder}
                isTranscribing={isTranscribing}
                voiceRecorderComponent={voiceRecorderButton}
                onChange={(value) => {
                  if (props.onlyDigits) {
                    value = parseProseMirrorSchema(value);
                  }

                  onChange(value);
                  if (props.onChange && typeof value === "string") {
                    props.onChange(props.fieldName, value, props.index);
                  }
                }}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
              />
            </div>
            {props.example && !props.fullExample && (
              <div className="ml-4 mt-1 text-sm text-black-600">
                {props.example}
              </div>
            )}
            {props.example && props.fullExample && (
              <div className="mx-4 mb-5">
                <Paragraph className="mt-2.5 mb-2 text-sm font-semibold text-black-600">
                  {t(
                    "application:controlledTextAreaWithRaisingLabel.text.example"
                  )}
                </Paragraph>
                <div className="flex flex-col gap-y-5 text-sm text-black-600">
                  {props.example}
                </div>
              </div>
            )}
          </>
        );
      }}
    />
  );
};

ControlledTextAreaWithRaisingLabel.displayName =
  "ControlledTextAreaWithRaisingLabel";

export default ControlledTextAreaWithRaisingLabel;
